import { DIGEST, SNAKE } from '../constants';
import type { Camera } from '../engine/Camera';
import type { Snake } from '../entities/Snake';
import { sampleSpline } from '../utils/spline';
import { clamp, type Vec2 } from '../utils/math';
import { drawPatternOverlay } from './patternOverlays';

/**
 * Monochromatic bioluminescent snake renderer.
 *
 * Strict identity rule: every snake is ONE hue. No hue interpolation, no
 * stripe in a different color. The "current" effect is produced by a
 * brightness wave that travels along the body — saturation stays maxed,
 * hue stays locked.
 *
 * Render passes per snake (all pure Canvas):
 *   1. Catmull-Rom spline samples through segments.
 *   2. Lateral swim wobble: each sample shifts perpendicular by a traveling
 *      sine wave — visual-only, doesn't affect collision math.
 *   3. Outer glow stroke (one fat blurred line, single hue at peak lightness).
 *   4. Body discs: each sample is a radial-gradient disc whose lightness is
 *      driven by a wave traveling along arclength — gives the "current
 *      flowing through the body" feel while keeping hue locked.
 *   5. Boost rim (when boosting): a hair-thin highlight in the same hue at
 *      ~85% lightness.
 *   6. Color-match head pulse halo: bright ring in the snake's own hue.
 *   7. Eyes — pupils track the steer target.
 *
 * Zero per-frame allocations: scratch buffers are instance-local.
 */
export class SnakeRenderer {
  private readonly samplesPerSeg = 4;
  private readonly lodPx = 6;

  private readonly samples: Vec2[] = [];
  private readonly xs: number[] = [];
  private readonly ys: number[] = [];

  /**
   * @param player Reference to the player snake — used to render danger
   *   indicators on rival heads (size-comparison arrow + a red pulse on
   *   any rival that's much bigger and close enough to threaten you).
   */
  draw(
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    snake: Snake,
    time: number,
    player?: Snake,
  ): void {
    if (!snake.alive || snake.segments.length < 2) return;

    // --- Frustum cull ---
    const bounds = camera.getVisibleBounds();
    const pad = snake.radius * 1.8;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (let i = 0; i < snake.segments.length; i++) {
      const s = snake.segments[i]!;
      if (s.x < minX) minX = s.x;
      if (s.y < minY) minY = s.y;
      if (s.x > maxX) maxX = s.x;
      if (s.y > maxY) maxY = s.y;
    }
    if (maxX + pad < bounds.minX || minX - pad > bounds.maxX) return;
    if (maxY + pad < bounds.minY || minY - pad > bounds.maxY) return;

    const z = camera.zoom;
    const headRadPx = snake.radius * z;
    const lod = headRadPx < this.lodPx;

    const density = lod ? 1 : this.samplesPerSeg;
    const samples = sampleSpline(snake.segments, density, this.samples);

    if (!lod) this.applyWobble(samples, snake, time);

    // --- Project to screen (shake-baked) ---
    const cx = camera.viewportW * 0.5 + camera.shakeX;
    const cy = camera.viewportH * 0.5 + camera.shakeY;
    const cax = camera.position.x;
    const cay = camera.position.y;
    const n = samples.length;
    const xs = this.xs;
    const ys = this.ys;
    while (xs.length < n) {
      xs.push(0);
      ys.push(0);
    }
    for (let i = 0; i < n; i++) {
      const s = samples[i]!;
      xs[i] = (s.x - cax) * z + cx;
      ys[i] = (s.y - cay) * z + cy;
    }

    if (lod) {
      this.drawLOD(ctx, snake, xs, ys, n);
      return;
    }

    // Spawn fade-in alpha: 0 → 1 over 0.25s after spawn.
    const sinceSpawn = time - snake.spawnTime;
    const spawnAlpha = Math.min(1, Math.max(0, sinceSpawn / 0.25));
    // Invincibility flicker: 0.4–1.0 alpha pulsing while invincible.
    const inv = snake.isInvincible(time);
    const invAlpha = inv ? 0.45 + 0.55 * Math.abs(Math.sin(time * 12)) : 1;
    const alpha = spawnAlpha * invAlpha;

    const needsAlpha = alpha < 0.999;
    if (needsAlpha) {
      ctx.save();
      ctx.globalAlpha = alpha;
    }

    // Alpha-leader aura — pulsing wide ring of light around the head of
    // the current #1 snake. Painted underneath the body so it reads as
    // "atmosphere" rather than a UI tag.
    if (snake.isLeader) this.drawLeaderAura(ctx, snake, xs[0]!, ys[0]!, headRadPx, time);

    // Shield indicator — bright cyan ring that pulses while the spawn
    // shield is active. The 0.45-pulse alpha modulation alone is too
    // subtle on a small newcomer snake; this ring tells the player
    // "you're protected" in 100 ms of glance time. Drawn beneath the
    // body so it surrounds the snake without obscuring the skin.
    if (snake.isInvincible(time)) {
      const remaining = snake.invincibleUntil - time;
      const pulse = 0.5 + 0.5 * Math.sin(time * 6);
      const ringR = headRadPx + 12 + pulse * 8;
      ctx.save();
      ctx.lineWidth = 3;
      ctx.strokeStyle = `hsla(190, 100%, 70%, ${0.55 + 0.25 * pulse})`;
      ctx.shadowColor = 'hsl(190 100% 75%)';
      ctx.shadowBlur = 18;
      ctx.beginPath();
      ctx.arc(xs[0]!, ys[0]!, ringR, 0, Math.PI * 2);
      ctx.stroke();
      // Countdown label so the player sees how many seconds are left
      // — without it the disappearance feels random when the shield
      // ends.
      if (remaining > 0.05) {
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = '600 11px "Space Grotesk", system-ui, sans-serif';
        ctx.fillStyle = 'hsla(190, 100%, 80%, 0.95)';
        ctx.shadowColor = 'hsla(190, 100%, 50%, 0.7)';
        ctx.shadowBlur = 6;
        ctx.fillText(`${remaining.toFixed(1)}s`, xs[0]!, ys[0]! - ringR - 12);
      }
      ctx.restore();
    }

    // Boost halo first (under the body) so the body floats over it.
    if (snake.boosting || snake.surgeRemaining > 0) this.drawBoostHalo(ctx, snake, xs, ys, n);

    this.drawFull(ctx, snake, xs, ys, n, time);
    // Gold-border halo — 60s-reward cosmetic. Drawn AFTER the body so
    // it sits on top as a bright amber outline that follows the
    // spine. Slow shimmer in alpha so it reads as an animated halo
    // and not a flat outline.
    if (snake.goldBorder) this.drawGoldBorder(ctx, snake, xs, ys, n, time);
    this.drawHead(ctx, snake, xs[0]!, ys[0]!, headRadPx, time);

    // Name label above head (skip for the player — they have HUD identity).
    if (!snake.isPlayer && headRadPx >= 10) {
      this.drawNameLabel(ctx, snake, xs[0]!, ys[0]!, headRadPx);
    }

    // Danger / size-comparison indicator — only when we have a reference
    // to the player snake AND this isn't the player itself.
    if (player && !snake.isPlayer && player.alive && headRadPx >= 8) {
      this.drawSizeIndicator(ctx, snake, player, xs[0]!, ys[0]!, headRadPx, time);
    }

    if (needsAlpha) ctx.restore();
  }

  /**
   * Tiny ▲/▼ icon above each rival's head + a pulsing red ring when the
   * rival is much bigger AND close to the player (close enough to be a
   * collision threat in the next ~1 second of normal travel).
   *
   * Reads quickly across the arena: every approach you see is annotated
   * with "I'm dangerous to you" or "I'm prey", removing the guesswork
   * that punishes new players (you can't tell who's bigger from
   * geometry alone when zoom keeps you visually constant).
   */
  private drawSizeIndicator(
    ctx: CanvasRenderingContext2D,
    rival: Snake,
    player: Snake,
    sx: number,
    sy: number,
    rPx: number,
    time: number,
  ): void {
    const ratio = rival.mass / Math.max(1, player.mass);
    // Distance in world units, between the two heads.
    const dx = rival.head.x - player.head.x;
    const dy = rival.head.y - player.head.y;
    const worldDist = Math.hypot(dx, dy);

    // Threat: much bigger AND nearby. Threshold ~700 world units (a
    // medium-sized snake's body length). Tuned to surface the indicator
    // about a second before a likely collision at normal speed.
    const isThreat = ratio >= 1.5 && worldDist < 700;

    // ── Threat pulse ring around the head ──
    if (isThreat) {
      const pulse = 0.55 + 0.45 * Math.sin(time * 5);
      ctx.save();
      ctx.strokeStyle = `hsla(0, 90%, 65%, ${0.55 + 0.35 * pulse})`;
      ctx.shadowColor = 'hsl(0, 100%, 70%)';
      ctx.shadowBlur = 12;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(sx, sy, rPx * (1.6 + 0.15 * pulse), 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    // ── Size-comparison glyph above the head ──
    // Skip when the sizes are roughly equal (0.85..1.15) — no useful info.
    if (ratio >= 0.85 && ratio <= 1.15) return;
    const bigger = ratio > 1.15;
    const glyph = bigger ? '▲' : '▼';
    const color = bigger ? 'hsl(0, 90%, 65%)' : 'hsl(135, 90%, 55%)';

    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `${Math.max(9, Math.round(rPx * 0.45))}px "Space Grotesk", system-ui, sans-serif`;
    ctx.fillStyle = color;
    ctx.shadowColor = color;
    ctx.shadowBlur = 6;
    // Position above the name label (which is at -rPx - 14).
    ctx.fillText(glyph, sx, sy - rPx - 26);
    ctx.restore();
  }

  // ---------------------------------------------------------------------------
  // Boost halo — a fat, blurred, faded outer stroke behind the body
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // Leader aura — the #1 snake on the leaderboard gets a pulsing crown halo
  // so they read as the alpha target across the arena.
  // ---------------------------------------------------------------------------

  private drawLeaderAura(
    ctx: CanvasRenderingContext2D,
    snake: Snake,
    sx: number,
    sy: number,
    rPx: number,
    time: number,
  ): void {
    const { hue } = snake.skin;
    // Slow heartbeat 0..1 — the aura pulses bright then dims.
    const beat = 0.55 + 0.45 * Math.sin(time * 2.4);
    const auraR = rPx * (2.6 + 0.5 * beat);
    ctx.save();
    ctx.shadowColor = `hsl(${hue} 100% 70%)`;
    ctx.shadowBlur = 26;
    // Soft outer fill — wide glow.
    const g = ctx.createRadialGradient(sx, sy, rPx * 0.8, sx, sy, auraR);
    g.addColorStop(0, `hsla(${hue} 100% 75% / ${0.18 + 0.12 * beat})`);
    g.addColorStop(0.55, `hsla(${hue} 100% 60% / ${0.10 + 0.10 * beat})`);
    g.addColorStop(1, `hsla(${hue} 100% 50% / 0)`);
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(sx, sy, auraR, 0, Math.PI * 2);
    ctx.fill();
    // Crisper ring on top — defines the silhouette.
    ctx.strokeStyle = `hsla(${hue} 100% 80% / ${0.45 + 0.4 * beat})`;
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    ctx.arc(sx, sy, rPx * (1.55 + 0.1 * beat), 0, Math.PI * 2);
    ctx.stroke();
    // Two orbiting sparks — give the aura a "crown of particles" feel
    // without needing a separate particle pool.
    const sparkCount = 4;
    for (let i = 0; i < sparkCount; i++) {
      const a = time * 1.5 + (i / sparkCount) * Math.PI * 2;
      const sr = rPx * (1.7 + 0.25 * Math.sin(time * 3 + i));
      const spx = sx + Math.cos(a) * sr;
      const spy = sy + Math.sin(a) * sr;
      ctx.fillStyle = `hsla(${hue} 100% 92% / 0.95)`;
      ctx.beginPath();
      ctx.arc(spx, spy, Math.max(1.4, rPx * 0.10), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  private drawBoostHalo(
    ctx: CanvasRenderingContext2D,
    snake: Snake,
    xs: number[],
    ys: number[],
    n: number,
  ): void {
    const { hue, sat, baseLight, lightAmp } = snake.skin;
    const haloLight = Math.min(85, baseLight + lightAmp + 8);
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    // Two-stroke halo: a wider faint stroke + a tighter brighter one
    // gives the boost an obvious "afterburn" silhouette without adding
    // a separate particle system. Both run in slowed time via simDt so
    // the visual remains in lockstep with the slow-mo on death.
    ctx.shadowColor = `hsla(${hue} ${sat}% ${haloLight}% / 0.85)`;

    // Outer wide halo.
    ctx.shadowBlur = 26;
    ctx.strokeStyle = `hsla(${hue} ${sat}% ${haloLight}% / 0.18)`;
    ctx.lineWidth = snake.widthAt(0.5) * 3.4;
    ctx.beginPath();
    ctx.moveTo(xs[0]!, ys[0]!);
    for (let i = 1; i < n; i++) ctx.lineTo(xs[i]!, ys[i]!);
    ctx.stroke();

    // Inner tight halo — brighter, defines the silhouette.
    ctx.shadowBlur = 14;
    ctx.strokeStyle = `hsla(${hue} ${sat}% ${haloLight}% / 0.45)`;
    ctx.lineWidth = snake.widthAt(0.5) * 2.0;
    ctx.beginPath();
    ctx.moveTo(xs[0]!, ys[0]!);
    for (let i = 1; i < n; i++) ctx.lineTo(xs[i]!, ys[i]!);
    ctx.stroke();
    ctx.restore();
  }

  /**
   * Gold-border halo — the 60s "session reward" cosmetic. Draws a
   * thin amber outline that hugs the body silhouette + a wider warm
   * glow underneath. Alpha shimmers slowly so the halo reads as
   * "champion's aura" rather than a static stroke. Always drawn AFTER
   * drawFull so the brand-hue body still shows through; the gold sits
   * on top as a frame.
   */
  private drawGoldBorder(
    ctx: CanvasRenderingContext2D,
    snake: Snake,
    xs: number[],
    ys: number[],
    n: number,
    time: number,
  ): void {
    // Slow shimmer (0.55..1.0 alpha over a ~1.6s sine cycle).
    const shimmer = 0.55 + 0.45 * (0.5 + 0.5 * Math.sin(time * 3.8));
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Wide soft underlight — establishes the "gold glow" footprint
    // before the crisp outline lands on top.
    ctx.shadowColor = 'rgba(255, 200, 80, 0.95)';
    ctx.shadowBlur = 22;
    ctx.strokeStyle = `rgba(255, 188, 50, ${0.22 * shimmer})`;
    ctx.lineWidth = snake.widthAt(0.5) * 2.6;
    ctx.beginPath();
    ctx.moveTo(xs[0]!, ys[0]!);
    for (let i = 1; i < n; i++) ctx.lineTo(xs[i]!, ys[i]!);
    ctx.stroke();

    // Crisp gold outline — slightly wider than the body radius so it
    // reads as a frame, not a recolor. Bright amber edge.
    ctx.shadowBlur = 6;
    ctx.strokeStyle = `rgba(255, 224, 130, ${0.85 * shimmer})`;
    ctx.lineWidth = snake.widthAt(0.5) * 1.9;
    ctx.beginPath();
    ctx.moveTo(xs[0]!, ys[0]!);
    for (let i = 1; i < n; i++) ctx.lineTo(xs[i]!, ys[i]!);
    ctx.stroke();

    ctx.restore();
  }

  // ---------------------------------------------------------------------------
  // Name labels — small text above each non-player snake's head
  // ---------------------------------------------------------------------------

  private drawNameLabel(
    ctx: CanvasRenderingContext2D,
    snake: Snake,
    sx: number,
    sy: number,
    rPx: number,
  ): void {
    const { hue } = snake.skin;
    const labelY = sy - rPx - 14;
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = '600 11px "Space Grotesk", system-ui, sans-serif';
    // Subtle dark outline for readability against bright pellets.
    ctx.strokeStyle = 'rgba(0,0,0,0.55)';
    ctx.lineWidth = 3;
    ctx.strokeText(snake.name, sx, labelY);
    ctx.fillStyle = `hsl(${hue} 100% 78%)`;
    ctx.shadowColor = `hsla(${hue} 100% 60% / 0.6)`;
    ctx.shadowBlur = 6;
    ctx.fillText(snake.name, sx, labelY);
    // Mass underline.
    ctx.shadowBlur = 0;
    ctx.font = '500 9px "JetBrains Mono", ui-monospace, monospace';
    ctx.fillStyle = 'rgba(255,255,255,0.55)';
    ctx.fillText(String(Math.round(snake.mass)), sx, labelY + 11);
    ctx.restore();
  }

  // ---------------------------------------------------------------------------
  // Wobble — visual-only lateral spine motion
  // ---------------------------------------------------------------------------

  private applyWobble(samples: Vec2[], snake: Snake, time: number): void {
    const n = samples.length;
    if (n < 2) return;
    const ampBase = snake.radius * SNAKE.WOBBLE_AMP;
    const freq = SNAKE.WOBBLE_FREQ;
    const speed = SNAKE.WOBBLE_SPEED;
    for (let i = 1; i < n; i++) {
      const prev = samples[i - 1]!;
      const cur = samples[i]!;
      const dx = cur.x - prev.x;
      const dy = cur.y - prev.y;
      const len = Math.hypot(dx, dy);
      if (len < 1e-6) continue;
      const px = -dy / len;
      const py = dx / len;
      const u = i / (n - 1);
      const amp = ampBase * (0.15 + u * 0.85);
      const phase = u * freq * Math.PI * 2 - time * speed * Math.PI * 2;
      const offset = Math.sin(phase) * amp;
      cur.x += px * offset;
      cur.y += py * offset;
    }
  }

  // ---------------------------------------------------------------------------
  // Render passes
  // ---------------------------------------------------------------------------

  private drawLOD(
    ctx: CanvasRenderingContext2D,
    snake: Snake,
    xs: number[],
    ys: number[],
    n: number,
  ): void {
    const { hue, sat, baseLight } = snake.skin;
    ctx.beginPath();
    ctx.moveTo(xs[0]!, ys[0]!);
    for (let i = 1; i < n; i++) ctx.lineTo(xs[i]!, ys[i]!);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = `hsl(${hue} ${sat}% ${baseLight}%)`;
    ctx.lineWidth = Math.max(1.5, snake.widthAt(0.5) * 1.2);
    ctx.stroke();
  }

  private drawFull(
    ctx: CanvasRenderingContext2D,
    snake: Snake,
    xs: number[],
    ys: number[],
    n: number,
    time: number,
  ): void {
    const {
      hue, sat, baseLight, lightAmp, waveFreq, waveSpeed, wavePhase,
      darkBandDepth, darkBandFreq, darkBandSpeed, darkBandPhase,
      orbCount, orbSpeed,
    } = snake.skin;
    const pulse = snake.pulse;
    const glowBoost = 1 + pulse * 1.4;
    const hasBands = darkBandDepth > 0.01 && darkBandFreq > 0.01;

    // Peak / dark anchor lightness — used by glow, body gradient and core line.
    const peakLight = clamp(baseLight + lightAmp + 6 + pulse * 14, 0, 95);
    const deepDark = clamp(baseLight - lightAmp * 0.6 - 16, 4, 50);

    // -------------------------------------------------------------------------
    // 1. Outer glow stroke — wide blurred halo that defines the silhouette.
    // -------------------------------------------------------------------------
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.shadowColor = `hsl(${hue} ${sat}% ${peakLight}%)`;
    ctx.shadowBlur = 22 * glowBoost;
    ctx.strokeStyle = `hsla(${hue} ${sat}% ${peakLight}% / ${0.45 + pulse * 0.35})`;
    ctx.lineWidth = snake.widthAt(0.5) * 2.5;
    ctx.beginPath();
    ctx.moveTo(xs[0]!, ys[0]!);
    for (let i = 1; i < n; i++) ctx.lineTo(xs[i]!, ys[i]!);
    ctx.stroke();
    ctx.restore();

    // -------------------------------------------------------------------------
    // 2. Body discs — high-contrast "neon tube" look:
    //    inner core nearly white-of-hue, outer rim deep dark variant.
    //    Brightness wave + optional dark bands modulate the inner brightness.
    //
    // shaderClock advances faster during boost (see Snake.update) so the
    // bioluminescent current visibly accelerates when the snake ignites.
    // -------------------------------------------------------------------------
    const phaseSpeed = waveSpeed * snake.shaderClock * Math.PI * 2;
    const bandPhaseSpeed = darkBandSpeed * snake.shaderClock * Math.PI * 2;
    for (let i = 0; i < n; i++) {
      const u = i / (n - 1);
      const r = Math.max(1.2, snake.widthAt(u));
      const phase = u * waveFreq * Math.PI * 2 - phaseSpeed + wavePhase;
      const tNorm = 0.5 + 0.5 * Math.sin(phase);   // 0..1

      // Inner: peak-bright near-white when wave is at peak.
      let innerLight = baseLight + lightAmp * 0.9 + lightAmp * 0.5 * tNorm + pulse * 14;
      // Outer: deep dark always — gives strong "tube" rim shadow.
      let outerLight = deepDark - 6 * u;

      // Dark-band overlay — multiplicative mask cuts brightness at band peaks.
      if (hasBands) {
        const bp = u * darkBandFreq * Math.PI * 2 - bandPhaseSpeed + darkBandPhase;
        const c = Math.cos(bp);
        const sharp = c * c;
        const mask = 1 - darkBandDepth * sharp;
        innerLight *= mask;
        outerLight *= mask;
      }

      innerLight = clamp(innerLight, 4, 96);
      outerLight = clamp(outerLight, 2, 80);

      const sx = xs[i]!;
      const sy = ys[i]!;
      const g = ctx.createRadialGradient(sx, sy, 0, sx, sy, r);
      g.addColorStop(0, `hsl(${hue} ${sat}% ${innerLight}%)`);
      g.addColorStop(0.55, `hsl(${hue} ${sat}% ${Math.max(outerLight, innerLight * 0.55)}%)`);
      g.addColorStop(1, `hsl(${hue} ${Math.min(100, sat + 5)}% ${outerLight}%)`);
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(sx, sy, r, 0, Math.PI * 2);
      ctx.fill();
    }

    // -------------------------------------------------------------------------
    // 3. Energy core line — thin near-white pulsing stroke along the spine.
    //    Universal accent that lifts every snake from "tube" to "live circuit".
    // -------------------------------------------------------------------------
    const coreBreath = 0.55 + 0.45 * Math.sin(snake.shaderClock * waveSpeed * Math.PI * 2 + wavePhase);
    const coreLight = clamp(peakLight + 8, 70, 96);
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.shadowColor = `hsl(${hue} 100% ${coreLight}%)`;
    ctx.shadowBlur = 10;
    ctx.strokeStyle = `hsla(${hue} 75% ${coreLight}% / ${0.45 + 0.35 * coreBreath + pulse * 0.2})`;
    ctx.lineWidth = Math.max(1.1, snake.widthAt(0.5) * 0.28);
    ctx.beginPath();
    ctx.moveTo(xs[0]!, ys[0]!);
    for (let i = 1; i < n; i++) ctx.lineTo(xs[i]!, ys[i]!);
    ctx.stroke();
    ctx.restore();

    // -------------------------------------------------------------------------
    // 4. Digesting orbs — pellets the snake recently ate, passing along
    //    the body. Each orb travels head→tail over ~4.5 s. Color matches
    //    the pellet (= snake's team hue on perfect matches).
    // -------------------------------------------------------------------------
    const orbs = snake.digestingOrbs;
    if (orbs.length > 0 && n >= 4) {
      const headWidth = snake.widthAt(0.5);
      ctx.save();
      ctx.shadowBlur = 14;
      for (let oi = 0; oi < orbs.length; oi++) {
        const orb = orbs[oi]!;
        const u = orb.u;
        // Gentle pulse so each orb breathes as it travels.
        const orbPulse = 0.6 + 0.4 * Math.sin(time * 4.5 + u * 6);
        // Sample along the spine for orb position.
        const idxF = u * (n - 1);
        const idx = Math.min(n - 2, Math.floor(idxF));
        const tFrac = idxF - idx;
        const sx = xs[idx]! * (1 - tFrac) + xs[idx + 1]! * tFrac;
        const sy = ys[idx]! * (1 - tFrac) + ys[idx + 1]! * tFrac;
        // Orb radius scales with the body width at its position so it
        // visibly "fits inside" the snake — bigger near head, slimmer near tail.
        const bodyW = snake.widthAt(u);
        const r = bodyW * DIGEST.ORB_RADIUS_FACTOR * (0.7 + 0.4 * orbPulse);
        const orbInnerLight = clamp(82 + 14 * orbPulse, 65, 96);
        // Hue from the eaten pellet — always equals the snake's own hue
        // since we only spawn orbs on perfect-color eats, but read it from
        // the orb to be future-proof.
        const oh = orb.hue;
        ctx.shadowColor = `hsl(${oh} 100% ${orbInnerLight}%)`;
        const g = ctx.createRadialGradient(sx, sy, 0, sx, sy, r);
        g.addColorStop(0, `hsl(${oh} 100% ${orbInnerLight}%)`);
        g.addColorStop(0.45, `hsl(${oh} 100% ${clamp(orbInnerLight - 22, 30, 80)}%)`);
        g.addColorStop(1, `hsla(${oh} 100% ${clamp(orbInnerLight - 40, 10, 60)}% / 0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(sx, sy, r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
      // Suppress unused-vars warning from the legacy skin orb fields —
      // they're kept on SnakeSkin for skin type compatibility but no
      // longer drive rendering (orbs are now gameplay-driven).
      void orbCount; void orbSpeed; void headWidth;
    }

    // -------------------------------------------------------------------------
    // 4b. Signature pattern overlay (Glitch / Plasma / Geometric / Bio-Neon).
    //     Drawn on top of the base body so the snake's identity is dominated
    //     by its pattern. Each rival is assigned a fixed pattern in World.ts.
    // -------------------------------------------------------------------------
    if (snake.skin.pattern) {
      drawPatternOverlay(ctx, snake.skin.pattern, {
        hue: snake.skin.hue,
        idSeed: snake.id,
        widthAt: (u: number) => snake.widthAt(u),
      }, xs, ys, n, time);
    }

    // -------------------------------------------------------------------------
    // 5. Boost rim — bright accent along the body when boosting.
    // -------------------------------------------------------------------------
    if (snake.boosting) {
      ctx.save();
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      const rimLight = clamp(baseLight + lightAmp + 24, 0, 96);
      ctx.strokeStyle = `hsla(${hue} ${sat}% ${rimLight}% / 0.85)`;
      ctx.lineWidth = 1.6;
      ctx.shadowColor = `hsl(${hue} ${sat}% ${rimLight}%)`;
      ctx.shadowBlur = 12;
      ctx.beginPath();
      ctx.moveTo(xs[0]!, ys[0]!);
      for (let i = 1; i < n; i++) ctx.lineTo(xs[i]!, ys[i]!);
      ctx.stroke();
      ctx.restore();
    }
  }


  private drawHead(
    ctx: CanvasRenderingContext2D,
    snake: Snake,
    sx: number,
    sy: number,
    rPxRaw: number,
    time: number,
  ): void {
    const head = snake.head;
    const seg1 = snake.segments[1] ?? head;
    const ang = Math.atan2(head.y - seg1.y, head.x - seg1.x);
    const { hue, sat, baseLight, lightAmp } = snake.skin;

    // Head breathing — gentle scale oscillation, frame-rate independent.
    // Adds a "creature is alive" beat to the head without affecting collision.
    const breath = 1 + 0.035 * Math.sin(time * 2.2);
    const rPx = rPxRaw * breath;

    // --- Tongue-flick: a thin forked tongue darts from the mouth ---
    // The tongue extends in the heading direction with a small forked tip,
    // appearing briefly then snapping back (decay handled in Snake.update).
    if (snake.tongue > 0.02) {
      const t = snake.tongue;
      const tongueLen = rPx * (1.1 + 1.0 * t);
      const tip = { x: sx + Math.cos(ang) * tongueLen, y: sy + Math.sin(ang) * tongueLen };
      const forkAng = 0.32; // fork half-angle, radians
      const forkLen = rPx * 0.45 * t;
      const fork1 = {
        x: tip.x + Math.cos(ang + forkAng) * forkLen,
        y: tip.y + Math.sin(ang + forkAng) * forkLen,
      };
      const fork2 = {
        x: tip.x + Math.cos(ang - forkAng) * forkLen,
        y: tip.y + Math.sin(ang - forkAng) * forkLen,
      };
      const mouthFrontX = sx + Math.cos(ang) * rPx * 0.5;
      const mouthFrontY = sy + Math.sin(ang) * rPx * 0.5;
      ctx.save();
      ctx.strokeStyle = '#ff4a6e';
      ctx.shadowColor = '#ff4a6e';
      ctx.shadowBlur = 4;
      ctx.lineWidth = Math.max(1.0, rPx * 0.10);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(mouthFrontX, mouthFrontY);
      ctx.lineTo(tip.x, tip.y);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(tip.x, tip.y);
      ctx.lineTo(fork1.x, fork1.y);
      ctx.moveTo(tip.x, tip.y);
      ctx.lineTo(fork2.x, fork2.y);
      ctx.stroke();
      ctx.restore();
    }

    // --- Mouth: Pac-Man-style wedge cut into the front of the head ---
    // Idle: slightly open (looks like a creature with a face). On chomp:
    // wedge opens wide, then snaps closed via the chomp exponential decay.
    // The wedge is filled with a near-black hue-tinted color, sitting on
    // top of the head body — reads as a real mouth.
    const chompOpen = 0.18 + 0.55 * snake.chomp;          // half-angle, rad
    const mouthDepth = rPx * (0.95 + 0.15 * snake.chomp); // depth into the head
    ctx.save();
    ctx.fillStyle = `hsl(${snake.skin.hue} 60% 6%)`;
    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.arc(sx, sy, mouthDepth, ang - chompOpen, ang + chompOpen, false);
    ctx.closePath();
    ctx.fill();
    // Thin tongue-tip during chomp — small bright slip at the wedge tip
    if (snake.chomp > 0.15) {
      const tongueOut = rPx * (0.45 + 0.4 * snake.chomp);
      const tx = sx + Math.cos(ang) * tongueOut;
      const ty = sy + Math.sin(ang) * tongueOut;
      ctx.fillStyle = `hsl(${snake.skin.hue} 90% 55% / 1)`;
      ctx.shadowColor = `hsl(${snake.skin.hue} 100% 60%)`;
      ctx.shadowBlur = 6;
      ctx.beginPath();
      ctx.arc(tx, ty, Math.max(1.2, rPx * 0.12), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    // --- Color-match pulse halo: smaller ring tightly hugging the head ---
    if (snake.pulse > 0.02) {
      const haloLight = clamp(baseLight + lightAmp + 20, 0, 92);
      ctx.save();
      ctx.globalAlpha = snake.pulse * 0.85;
      ctx.strokeStyle = `hsl(${hue} ${sat}% ${haloLight}%)`;
      ctx.shadowColor = `hsl(${hue} ${sat}% ${haloLight}%)`;
      ctx.shadowBlur = 14;
      ctx.lineWidth = 1.4 + 2.8 * snake.pulse;
      ctx.beginPath();
      ctx.arc(sx, sy, rPx * (1.05 + 0.25 * (1 - snake.pulse)), 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    // --- Eyes: reptilian — dark hue-tinted ellipse + VERTICAL SLIT pupil ---
    // No white sclera (that's what made the previous design read as human).
    // Instead: a small dark eye in the snake's hue, with a thin vertical slit
    // pupil that tracks the steer direction. Reads as snake/cat — menacing.
    const eyeR = Math.max(1.6, rPx * 0.18);          // smaller than before
    const eyeAspectW = 1.15;                          // eyes slightly wide
    const slitW = Math.max(0.5, eyeR * 0.20);         // pupil width (slim)
    const slitH = Math.max(1.0, eyeR * 0.72);         // pupil height (tall)
    const perpX = -Math.sin(ang);
    const perpY = Math.cos(ang);
    const forwardX = Math.cos(ang);
    const forwardY = Math.sin(ang);
    const eyeOffset = rPx * 0.42;
    const eyeForward = rPx * 0.30;

    const dx = snake.steerTarget.x - head.x;
    const dy = snake.steerTarget.y - head.y;
    const dl = Math.hypot(dx, dy);
    const tx = dl > 1e-3 ? dx / dl : forwardX;
    const ty = dl > 1e-3 ? dy / dl : forwardY;

    for (const side of [-1, 1] as const) {
      const ex = sx + perpX * eyeOffset * side + forwardX * eyeForward;
      const ey = sy + perpY * eyeOffset * side + forwardY * eyeForward;
      // Eye body — slightly wide ellipse in dark hue-tint with a soft
      // bright rim at the top edge (subtle scale-like sheen).
      ctx.save();
      ctx.translate(ex, ey);
      ctx.rotate(ang);
      // Outer dark eye
      ctx.fillStyle = `hsl(${hue} 60% 14%)`;
      ctx.beginPath();
      ctx.ellipse(0, 0, eyeR * eyeAspectW, eyeR, 0, 0, Math.PI * 2);
      ctx.fill();
      // Thin lighter rim — gives 3D dome feel without the human-sclera look
      ctx.strokeStyle = `hsla(${hue} 80% 60% / 0.6)`;
      ctx.lineWidth = 0.7;
      ctx.beginPath();
      ctx.ellipse(0, 0, eyeR * eyeAspectW, eyeR, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();

      // Vertical-slit pupil — drawn in world (un-rotated) space, tracking
      // the steer target. Pupil position offsets toward steer direction by
      // a small amount so the eyes "follow" the cursor.
      const pupilCx = ex + tx * eyeR * 0.35;
      const pupilCy = ey + ty * eyeR * 0.35;
      ctx.save();
      ctx.fillStyle = '#000000';
      ctx.beginPath();
      // Slit is oriented along the snake's vertical (perpendicular to heading).
      // That way it stays "vertical relative to the snake" even when the
      // snake faces sideways — like a real cat/snake eye.
      ctx.ellipse(pupilCx, pupilCy, slitW, slitH, ang + Math.PI / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }
}
